import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const defaultTransactionForm = {
  description: '',
  direction: 'debit',
  amount: '',
  booked_at: '',
  merchant_name: '',
  category: '',
};

function formatDate(value) {
  return value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : 'Nao informado';
}

const PersonalFinanceAccountsPage = () => {
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(defaultTransactionForm);

  const loadDetail = async (accountId) => {
    if (!accountId) {
      setDetail(null);
      return;
    }

    try {
      setDetailLoading(true);
      const response = await axios.get(`/api/personal-finance/accounts/${accountId}`);
      setDetail(response.data || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar detalhe da conta');
    } finally {
      setDetailLoading(false);
    }
  };

  const loadAccounts = async (preferredAccountId = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/accounts');
      const nextAccounts = Array.isArray(response.data) ? response.data : [];
      const nextSelectedId =
        preferredAccountId ||
        (nextAccounts.some((item) => item.id === selectedAccountId) ? selectedAccountId : nextAccounts[0]?.id || '');

      setAccounts(nextAccounts);
      setSelectedAccountId(nextSelectedId);
      await loadDetail(nextSelectedId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const selectedAccount = accounts.find((item) => item.id === selectedAccountId) || null;

  const handleCreateTransaction = async () => {
    if (!selectedAccountId) return;

    try {
      setSaving(true);
      await axios.post(`/api/personal-finance/accounts/${selectedAccountId}/transactions`, {
        description: form.description,
        direction: form.direction,
        amount: Number(form.amount || 0),
        booked_at: form.booked_at || undefined,
        merchant_name: form.merchant_name || undefined,
        category: form.category || undefined,
      });
      toast.success('Transacao criada');
      setForm(defaultTransactionForm);
      await loadAccounts(selectedAccountId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar transacao');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Contas ativas</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : String(accounts.length)}</p>
            <p className="text-sm text-slate-500">Visao operacional do caixa conectado</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Saldo atual</p>
            <p className="text-2xl font-semibold text-slate-900">
              {selectedAccount ? currency.format(Number(selectedAccount.current_balance || 0)) : '--'}
            </p>
            <p className="text-sm text-slate-500">Base atual da conta selecionada</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Saldo disponivel</p>
            <p className="text-2xl font-semibold text-slate-900">
              {selectedAccount?.available_balance === null || selectedAccount?.available_balance === undefined
                ? '--'
                : currency.format(Number(selectedAccount.available_balance || 0))}
            </p>
            <p className="text-sm text-slate-500">Liquidez imediata na conta selecionada</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.35fr]">
        <div className="card">
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Operating layer</p>
                <h2 className="text-xl font-semibold text-slate-900">Contas</h2>
              </div>
              <button className="btn-secondary" onClick={() => loadAccounts(selectedAccountId)} disabled={loading}>
                Atualizar
              </button>
            </div>

            {!loading && accounts.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                Nenhuma conta canonica disponivel. Conecte uma base ou faca seed manual em `Conexoes`.
              </div>
            )}

            <div className="space-y-3">
              {accounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    selectedAccountId === account.id ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  onClick={() => {
                    setSelectedAccountId(account.id);
                    loadDetail(account.id);
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{account.display_name}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {account.institution_name || account.connection_display_name} / {account.account_type}
                      </p>
                    </div>
                    <span className="badge-info">{account.currency}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-1 text-sm text-slate-600">
                    <p>Saldo atual: {currency.format(Number(account.current_balance || 0))}</p>
                    <p>
                      Saldo disponivel:{' '}
                      {account.available_balance === null || account.available_balance === undefined
                        ? 'Nao informado'
                        : currency.format(Number(account.available_balance || 0))}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Detalhe</p>
                <h2 className="text-xl font-semibold text-slate-900">
                  {selectedAccount ? selectedAccount.display_name : 'Selecione uma conta'}
                </h2>
              </div>

              {!selectedAccount && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                  Escolha uma conta para ver saldo, fluxo recente e operar transacoes mockadas.
                </div>
              )}

              {selectedAccount && (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Entradas</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">
                        {detailLoading ? '...' : currency.format(Number(detail?.stats?.credits || 0))}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Saidas</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">
                        {detailLoading ? '...' : currency.format(Number(detail?.stats?.debits || 0))}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Fluxo liquido</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">
                        {detailLoading ? '...' : currency.format(Number(detail?.stats?.net_flow || 0))}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Descricao</span>
                      <input
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                        value={form.description}
                        onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                        placeholder="Mercado, salario, transferencia"
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Direcao</span>
                      <select
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                        value={form.direction}
                        onChange={(event) => setForm((current) => ({ ...current, direction: event.target.value }))}
                      >
                        <option value="debit">debit</option>
                        <option value="credit">credit</option>
                      </select>
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Valor</span>
                      <input
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.amount}
                        onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Data</span>
                      <input
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                        type="date"
                        value={form.booked_at}
                        onChange={(event) => setForm((current) => ({ ...current, booked_at: event.target.value }))}
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Merchant</span>
                      <input
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                        value={form.merchant_name}
                        onChange={(event) => setForm((current) => ({ ...current, merchant_name: event.target.value }))}
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Categoria</span>
                      <input
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                        value={form.category}
                        onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                      />
                    </label>
                  </div>

                  <button className="btn-primary" onClick={handleCreateTransaction} disabled={saving}>
                    {saving ? 'Salvando...' : 'Adicionar transacao mockada'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Historico</p>
                <h2 className="text-xl font-semibold text-slate-900">Transacoes da conta</h2>
              </div>

              {!selectedAccount && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                  Selecione uma conta para ver as transacoes.
                </div>
              )}

              {selectedAccount && (
                <div className="space-y-3">
                  {detailLoading && <p className="text-sm text-slate-500">Carregando transacoes...</p>}
                  {!detailLoading && (!detail?.transactions || detail.transactions.length === 0) && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhuma transacao mockada registrada para esta conta.
                    </div>
                  )}
                  {(detail?.transactions || []).map((transaction) => {
                    const merchantLabel = transaction.normalized_merchant_name || transaction.merchant_name;
                    const categoryLabel = transaction.normalized_category || transaction.category;

                    return (
                      <div key={transaction.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">{transaction.description}</p>
                            <p className="mt-1 text-sm text-slate-500">
                              {formatDate(transaction.booked_at)}
                              {merchantLabel ? ` / ${merchantLabel}` : ''}
                            </p>
                            {transaction.normalized_merchant_name && transaction.merchant_name && transaction.normalized_merchant_name !== transaction.merchant_name && (
                              <p className="mt-1 text-xs text-slate-400">Origem: {transaction.merchant_name}</p>
                            )}
                          </div>
                          <span className={transaction.direction === 'credit' ? 'badge-success' : 'badge-warning'}>
                            {transaction.direction}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                          <span>Valor: {currency.format(Number(transaction.amount || 0))}</span>
                          <span>Status: {transaction.status}</span>
                          <span>Categoria: {categoryLabel || 'Nao informada'}</span>
                          {transaction.normalized_category && transaction.category && transaction.normalized_category !== transaction.category && (
                            <span>Categoria original: {transaction.category}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceAccountsPage;
